import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import { ChevronDownIcon } from './common/svgs/svgs';
import CloseCircleIcon from '../assets/images/svgs/close-circle.svg';

interface DropdownPillOption {
  value: string | number | undefined;
  label: string;
  icon?: React.ReactNode;
}

interface DropdownPillProps {
  label: string;
  options: DropdownPillOption[];
  selectedValue?: string | number | undefined;
  onSelect: (value: string | number | undefined) => void;
  placeholder?: string;
  className?: string;
}

export const DropdownPill: React.FC<DropdownPillProps> = ({
  label,
  options,
  selectedValue,
  onSelect,
  placeholder,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = useMemo(() => {
    // Only find option if selectedValue is not undefined
    // This prevents matching the "All" option when nothing is selected
    if (selectedValue === undefined) {
      return null;
    }
    return options.find((opt) => opt.value === selectedValue);
  }, [options, selectedValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleOptionClick = useCallback(
    (value: string | number | undefined) => {
      onSelect(value);
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(undefined);
    },
    [onSelect],
  );

  // If a value is selected, show as orange pill with close circle button
  if (selectedOption) {
    return (
      <div ref={dropdownRef} className={`relative inline-flex ${className}`}>
        <div
          onClick={handleClear}
          className="bg-accent hover:bg-accent-hover inline-flex cursor-pointer select-none items-center justify-center gap-1.5 rounded-full px-7 py-3"
        >
          <img src={CloseCircleIcon} alt="Clear filter" className="h-4 w-4" />
          <div className="font-albert-sans text-base font-medium leading-5 text-white">{selectedOption.label}</div>
        </div>
      </div>
    );
  }

  // If no value selected, show as regular dropdown button
  return (
    <div ref={dropdownRef} className={`relative inline-flex flex-col items-start justify-start gap-4 ${className}`}>
      <button
        onClick={handleToggle}
        className="border-1 bg-button-secondary hover:bg-button-secondary-hover inline-flex items-center justify-center gap-1.5 rounded-full px-7 py-3"
      >
        <div className="font-albert-sans text-primary text-base font-medium leading-5">{placeholder || label}</div>
        <div className="flex h-5 w-5 items-center justify-center">
          <ChevronDownIcon className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {isOpen && (
        <div className="absolute top-full z-10 mt-2 max-h-96 w-64 overflow-y-auto rounded-lg border border-gray-300 bg-white">
          {options.map((option) => (
            <button
              key={option.value ?? 'all'}
              onClick={() => handleOptionClick(option.value)}
              className="hover:bg-button-secondary focus:bg-button-secondary flex w-full items-center gap-2 px-4 py-2 text-left focus:outline-none"
            >
              {option.icon && <div className="flex h-5 w-5 shrink-0 items-center justify-center">{option.icon}</div>}
              <div className="font-albert-sans text-primary text-sm font-medium">{option.label}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
